import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConfigFieldOption } from '@feathr/blackbox';
import { Button, CurrencyEntry, Icon, Input, Well } from '@feathr/components';

import { useDonationFormBuilder } from '../../../useDonationFormBuilder';

interface IProps {
  option: ConfigFieldOption;
}

function SuggestedAmount({ option }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { minValue, allowDescriptions } = useDonationFormBuilder();
  const {
    spacing: { '2': gap },
  } = useMantineTheme();
  const [descriptionTouched, setDescriptionTouched] = useState(false);

  function handleChangeValue(value?: string | number): void {
    option.set({ value: value?.toString() ?? '' });
  }

  function handleChangeDescription(value?: string): void {
    setDescriptionTouched(true);
    option.set({ description: value ?? '' });
  }

  function handleDescriptionBlur(): void {
    setDescriptionTouched(true);
  }

  const amountError =
    option.value && minValue && Number(option.value) < minValue
      ? t('Amount must be at least {{min}}', { min: minValue })
      : undefined;

  // Show error if descriptions are enabled and field is blank after being touched
  const descriptionError =
    allowDescriptions && descriptionTouched && !option.description?.trim()
      ? t('Description is required')
      : undefined;

  return (
    <Well layout={'vertical'}>
      <Flex align={'center'} gap={gap} justify={'flex-start'}>
        <CurrencyEntry
          error={amountError}
          min={minValue}
          onChange={handleChangeValue}
          value={option.value}
          valueIsNumericString={true}
        />
        <Button onClick={option.delete} type={'icon-small'}>
          <Icon.Clear />
        </Button>
      </Flex>
      {allowDescriptions && (
        <Input
          onBlur={handleDescriptionBlur}
          onChange={handleChangeDescription}
          placeholder={t('Add a description')}
          required={true}
          type={'text'}
          validationError={descriptionError}
          value={option.description}
        />
      )}
    </Well>
  );
}

export default observer(SuggestedAmount);
